import { useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Hook for kicking off a heavy backend LLM generation as a background task
 * and polling its status until it finishes.
 *
 * Usage:
 *   const { run, status, result, error, elapsedSec, isRunning } = useLLMTask();
 *   await run({
 *     endpoint: `/automation/playbooks/generate-async/${brand}/${region}`,
 *     onDone: (result) => setPlaybook(result.playbook),
 *   });
 *
 * The backend endpoint must return `{ task_id }` with HTTP 202; the hook
 * then polls `GET /api/llm-tasks/{task_id}` every `pollIntervalMs` until
 * `status` is one of `done`, `error`, or `timeout`.
 */
export function useLLMTask({ pollIntervalMs = 4000, maxWaitMs = 300000 } = {}) {
  const [status, setStatus] = useState("idle"); // idle | pending | running | done | error | timeout
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [elapsedSec, setElapsedSec] = useState(0);

  const pollRef = useRef(null);
  const tickRef = useRef(null);
  const cancelledRef = useRef(false);

  // Tick the elapsed counter once per second while a task is running
  useEffect(() => {
    if (!startedAt) {
      setElapsedSec(0);
      if (tickRef.current) clearInterval(tickRef.current);
      return undefined;
    }
    tickRef.current = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [startedAt]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    cancelledRef.current = true;
    stopPolling();
    setStartedAt(null);
    setStatus("idle");
  }, [stopPolling]);

  const run = useCallback(
    async ({ endpoint, body, onDone, onError, method = "post" }) => {
      cancelledRef.current = false;
      setStatus("pending");
      setResult(null);
      setError(null);
      setStartedAt(Date.now());

      try {
        const res = await axios[method](`${API}${endpoint}`, body || {});
        const newTaskId = res?.data?.task_id;
        if (!newTaskId) {
          throw new Error("Backend did not return a task_id");
        }
        setTaskId(newTaskId);
        setStatus("running");

        // Begin polling
        const startedTs = Date.now();
        pollRef.current = setInterval(async () => {
          if (cancelledRef.current) {
            stopPolling();
            return;
          }
          if (Date.now() - startedTs > maxWaitMs) {
            stopPolling();
            setStatus("timeout");
            setStartedAt(null);
            const msg = "Generation took too long. Please try again.";
            setError(msg);
            toast.error("AI generation timed out", { description: msg });
            onError?.(msg);
            return;
          }

          try {
            const pollRes = await axios.get(`${API}/llm-tasks/${newTaskId}`);
            const data = pollRes.data;
            setStatus(data.status);
            if (data.status === "done") {
              stopPolling();
              setResult(data.result);
              setStartedAt(null);
              toast.success("AI generation complete");
              onDone?.(data.result);
            } else if (data.status === "error" || data.status === "timeout") {
              stopPolling();
              setError(data.error || "Generation failed");
              setStartedAt(null);
              toast.error(
                data.status === "timeout" ? "AI generation timed out" : "AI generation failed",
                { description: data.error || "An unknown error occurred." }
              );
              onError?.(data.error);
            }
          } catch (pollErr) {
            // Transient poll error — keep polling, but log
            console.warn("Poll error:", pollErr);
          }
        }, pollIntervalMs);
      } catch (err) {
        stopPolling();
        setStartedAt(null);
        const msg = err?.response?.data?.detail || err.message || "Failed to start task";
        setStatus("error");
        setError(msg);
        toast.error("Failed to start generation", { description: msg });
        onError?.(msg);
      }
    },
    [pollIntervalMs, maxWaitMs, stopPolling]
  );

  // Cleanup on unmount
  useEffect(() => () => stopPolling(), [stopPolling]);

  return {
    run,
    cancel,
    status,
    result,
    error,
    taskId,
    elapsedSec,
    isRunning: status === "pending" || status === "running",
  };
}
